import type { KeyboardEvent, RefObject } from "react";
import { useMemo, useRef, useState } from "react";

import type { Textfield } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    COMPETITOR_BRAND_ERROR_KEYS,
    type ICompetitorBrandError,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/competitor-section/types";
import { validateCompetitorBrand } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/brandValidation";

interface IUseBrandDraftParams {
    existingBrands: string[];
    onAdd: (brand: string) => void;
    duplicateErrorKey?: string;
}

interface IUseBrandDraftResult {
    brandFieldRef: RefObject<Textfield | null>;
    brandDraft: string;
    setBrandDraft: (value: string) => void;
    trimmedBrand: string;
    brandValidationError: ICompetitorBrandError | null;
    brandDraftErrorMessage: string;
    canAddBrand: boolean;
    addBrand: () => void;
    commitBrand: (brand: string) => void;
    onBrandKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export const useBrandDraft = ({
    existingBrands,
    onAdd,
    duplicateErrorKey,
}: IUseBrandDraftParams): IUseBrandDraftResult => {
    const [brandDraft, setBrandDraft] = useState<string>("");
    const brandFieldRef = useRef<Textfield>(null);
    const { trimmedBrand, brandValidationError } = useMemo(
        () => validateCompetitorBrand(brandDraft, existingBrands),
        [brandDraft, existingBrands],
    );

    const brandDraftErrorMessage = useMemo<string>(() => {
        if (!brandValidationError || !trimmedBrand) {
            return "";
        }

        const errorKey =
            brandValidationError.key === COMPETITOR_BRAND_ERROR_KEYS.duplicate && duplicateErrorKey
                ? duplicateErrorKey
                : brandValidationError.key;

        return translate(errorKey, brandValidationError.params);
    }, [brandValidationError, trimmedBrand, duplicateErrorKey]);

    const canAddBrand = !brandValidationError;

    const addBrand = (): void => {
        if (brandValidationError) {
            return;
        }

        onAdd(trimmedBrand);
        setBrandDraft("");
        brandFieldRef.current?.clearValue?.();
    };

    const commitBrand = (brand: string): void => {
        const { trimmedBrand: validBrand, brandValidationError: validationError } = validateCompetitorBrand(
            brand,
            existingBrands,
        );

        if (validationError) {
            setBrandDraft(brand);
            return;
        }

        onAdd(validBrand);
        setBrandDraft("");
        brandFieldRef.current?.clearValue?.();
    };

    const onBrandKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === "Enter") {
            event.preventDefault();
            addBrand();
        }
    };

    return {
        brandFieldRef,
        brandDraft,
        setBrandDraft,
        trimmedBrand,
        brandValidationError,
        brandDraftErrorMessage,
        canAddBrand,
        addBrand,
        commitBrand,
        onBrandKeyDown,
    };
};
